export type PlanningRecommendationType =
  | 'no_future_match'
  | 'not_played_recently'
  | 'low_home_percentage'
  | 'projected_high_next_week'
  | 'projected_below_target'
  | 'lineup_load'
  | 'target_unreachable'
  | 'target_at_risk'
  | 'scarce_opportunity'
  | 'participation_gap'
  | 'available_not_selected'
  | 'maybe_assignment'
  | 'workload';

export type PlanningRecommendationInput = {
  teamId: string;
  seasonId: string;
  playerId: string;
  playerName: string;
  firstName?: string;
  lastName?: string;
  totalPlayed: number;
  futureSingles: number;
  futureDoubles: number;
  statusDelta: number;
  lastPlayedWeeksAgo: number | null;
  nextMatchWeeksAway: number | null;
  homeMatches: number;
  awayMatches: number;
  neutralMatches: number;
  unknownSiteMatches: number;
  totalCompletedAndFutureScheduledMatches: number;
  homeMatchPercentage: number | null;
};

export type PlanningRecommendation = {
  key: string;
  baseKey: string;
  recommendationType: PlanningRecommendationType;
  teamId: string;
  seasonId: string;
  playerId: string;
  stateSignature: string;
  message: string;
  priority: 'high' | 'medium' | 'low';
  impact: number;
};

export type DismissalRow = {
  notification_key: string;
  dismissed_by: string | null;
};

function recommendationPlayerName(row: PlanningRecommendationInput): string {
  const parsed = row.playerName.trim().split(/\s+/);
  const firstName = row.firstName ?? parsed[0] ?? row.playerName;
  const lastName = row.lastName ?? parsed.slice(1).join(' ');
  const lastInitial = lastName.trim().charAt(0);
  if (firstName && lastInitial) return `${firstName} ${lastInitial}.`;
  return firstName || lastName || row.playerName;
}

function deficitLabel(deficit: number): string {
  return `${deficit} match${deficit === 1 ? '' : 'es'} below target`;
}

function statePart(value: string): string {
  return encodeURIComponent(value.toLowerCase());
}

export function planningRecommendationBaseKey(
  recommendationType: PlanningRecommendationType,
  teamId: string,
  seasonId: string,
  playerId: string,
): string {
  return `planning:${recommendationType}:${teamId}:${seasonId}:${playerId}`;
}

export function planningRecommendationKey(
  recommendationType: PlanningRecommendationType,
  teamId: string,
  seasonId: string,
  playerId: string,
  stateSignature: string,
): string {
  return `${planningRecommendationBaseKey(recommendationType, teamId, seasonId, playerId)}:state:${statePart(stateSignature)}`;
}

function recommendation(
  row: PlanningRecommendationInput,
  recommendationType: PlanningRecommendationType,
  stateSignature: string,
  message: string,
  priority: PlanningRecommendation['priority'],
  impact: number,
): PlanningRecommendation {
  return {
    key: planningRecommendationKey(recommendationType, row.teamId, row.seasonId, row.playerId, stateSignature),
    baseKey: planningRecommendationBaseKey(recommendationType, row.teamId, row.seasonId, row.playerId),
    recommendationType,
    teamId: row.teamId,
    seasonId: row.seasonId,
    playerId: row.playerId,
    stateSignature,
    message,
    priority,
    impact,
  };
}

export function buildPlanningRecommendations(rows: PlanningRecommendationInput[]): PlanningRecommendation[] {
  const recommendations = rows.flatMap((row) => {
    const name = recommendationPlayerName(row);
    const futureTotal = row.futureSingles + row.futureDoubles;
    const deficit = Math.max(0, Math.abs(Math.min(row.statusDelta, 0)));
    const recommendations: PlanningRecommendation[] = [];

    if (row.lastPlayedWeeksAgo != null && row.lastPlayedWeeksAgo >= 3 && row.statusDelta < 0) {
      recommendations.push(recommendation(
        row,
        'not_played_recently',
        `weeks=${row.lastPlayedWeeksAgo};status=${row.statusDelta}`,
        `${name} has not played in ${row.lastPlayedWeeksAgo} weeks and is projected ${deficitLabel(deficit)}.`,
        deficit >= 2 ? 'high' : 'medium',
        deficit,
      ));
    }
    if (row.nextMatchWeeksAway == null && row.statusDelta < 0) {
      recommendations.push(recommendation(
        row,
        'no_future_match',
        `future=0;status=${row.statusDelta};played=${row.totalPlayed}`,
        `${name} has no future matches scheduled and is projected ${deficitLabel(deficit)}.`,
        'high',
        deficit,
      ));
    }
    if (row.homeMatchPercentage != null && row.totalCompletedAndFutureScheduledMatches >= 4 && row.homeMatchPercentage <= 25) {
      recommendations.push(recommendation(
        row,
        'low_home_percentage',
        `home=${row.homeMatches};away=${row.awayMatches};neutral=${row.neutralMatches};unknown=${row.unknownSiteMatches};total=${row.totalCompletedAndFutureScheduledMatches};pct=${row.homeMatchPercentage}`,
        `${name} has only ${row.homeMatchPercentage}% home assignments.`,
        row.homeMatchPercentage <= 20 ? 'high' : 'low',
        100 - row.homeMatchPercentage,
      ));
    }
    if (row.statusDelta > 0 && row.nextMatchWeeksAway != null && row.nextMatchWeeksAway <= 1) {
      recommendations.push(recommendation(
        row,
        'projected_high_next_week',
        `status=${row.statusDelta};next=${row.nextMatchWeeksAway}`,
        `${name} is projected high and already scheduled ${row.nextMatchWeeksAway === 0 ? 'this week' : 'next week'}.`,
        'medium',
        row.statusDelta,
      ));
    }
    if (row.statusDelta < 0 && row.nextMatchWeeksAway != null) {
      recommendations.push(recommendation(
        row,
        'projected_below_target',
        `status=${row.statusDelta};played=${row.totalPlayed};future=${futureTotal}`,
        `${name} is projected ${deficitLabel(deficit)}.`,
        deficit >= 2 ? 'high' : 'medium',
        deficit,
      ));
    }

    return recommendations;
  });
  const priorityRank: Record<PlanningRecommendation['priority'], number> = { high: 0, medium: 1, low: 2 };
  return recommendations.sort((a, b) => {
    const priority = priorityRank[a.priority] - priorityRank[b.priority];
    if (priority) return priority;
    const impact = b.impact - a.impact;
    if (impact) return impact;
    return a.message.localeCompare(b.message, undefined, { sensitivity: 'base' });
  });
}

export function visiblePlanningRecommendations(
  recommendations: PlanningRecommendation[],
  dismissedKeys: Set<string>,
  limit = 8,
): PlanningRecommendation[] {
  return recommendations.filter((recommendation) => !dismissedKeys.has(recommendation.key)).slice(0, limit);
}

export function dismissedPlanningRecommendationKeysForUser(rows: DismissalRow[], userId: string | null): Set<string> {
  return new Set(
    rows
      .filter((row) => row.dismissed_by === userId)
      .map((row) => row.notification_key)
      .filter((key) => key.startsWith('planning:')),
  );
}
